/* Vidéos produit : carrousel de cartes, puis lecteur plein écran.
   Refait le 2026-09-25. Un seul élément <video> sert tout le lecteur : il passe d'une vidéo à
   l'autre. Sur iPhone, c'est ce qui garde le son actif après le premier toucher; avec une vidéo
   par écran, chaque nouvelle vidéo repartirait muette.
   Corrigé le 2026-09-25 : le lecteur lit toujours le fichier MP4, jamais le flux HLS. Sur iPhone,
   le HLS change de qualité pendant la première seconde et Safari dessinait alors une petite copie
   de la vidéo par-dessus une image figée (vu par Jimmy sur son téléphone). Le MP4 garde une seule
   taille du début à la fin, comme l'ancienne section vidéos et la galerie du haut. */
class VideoGalleryGrand extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.track = this.querySelector(".pvg__track");
    this.dialog = this.querySelector(".pvg-player");
    if (!this.track || !this.dialog) return;
    this.pret = true;

    this.cartes = [...this.querySelectorAll(".pvg__card")];
    this.slider = this.dialog.querySelector(".pvg-player__slider");
    this.slides = [...this.dialog.querySelectorAll(".pvg-player__slide")];
    this.compteur = this.dialog.querySelector("[data-current]");
    this.btnSon = this.dialog.querySelector(".pvg-player__sound");
    this.btnPrec = this.dialog.querySelector('[data-step="-1"]');
    this.btnSuiv = this.dialog.querySelector('[data-step="1"]');
    this.indice = -1;
    this.muet = false;

    const economie = navigator.connection && navigator.connection.saveData;
    this.calme = economie || matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.video = document.createElement("video");
    this.video.className = "pvg-player__video";
    this.video.playsInline = true;
    this.video.setAttribute("playsinline", "");
    this.video.preload = "auto";

    this.initCartes();
    this.initFleches();
    this.initLecteur();
  }

  /* Les cartes : l'aperçu muet ne joue que si la carte est visible aux deux tiers. */
  initCartes() {
    this.cartes.forEach((carte, i) => {
      carte.addEventListener("click", () => this.ouvrir(i, carte));
      const apercu = carte.querySelector(".pvg__preview");
      if (apercu) apercu.addEventListener("playing", () => apercu.classList.add("is-live"));
    });
    if (this.calme || !("IntersectionObserver" in window)) return;

    this.observateurCartes = new IntersectionObserver((entrees) => {
      entrees.forEach((entree) => {
        const apercu = entree.target.querySelector(".pvg__preview");
        if (!apercu) return;
        if (entree.isIntersecting && !this.dialog.open) {
          this.prechargerAffiches();
          this.charger(apercu);
          apercu.play().catch(() => {});
        } else {
          apercu.pause();
        }
      });
    }, { threshold: 0.66 });
    this.cartes.forEach((carte) => this.observateurCartes.observe(carte));
  }

  charger(video) {
    if (video.dataset.charge) return;
    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
    });
    video.dataset.charge = "1";
    video.load();
  }

  /* Les images du lecteur dorment tant qu'il est fermé : on les demande dès que les cartes sont
     à l'écran, pour qu'aucun écran noir ne passe à l'ouverture. */
  prechargerAffiches() {
    if (this.affichesPretes) return;
    this.affichesPretes = true;
    this.dialog.querySelectorAll(".pvg-player__poster").forEach((img) => {
      img.loading = "eager";
    });
  }

  pauserApercus() {
    this.cartes.forEach((carte) => {
      const apercu = carte.querySelector(".pvg__preview");
      if (apercu) apercu.pause();
    });
  }

  reprendreApercus() {
    if (!this.observateurCartes) return;
    this.cartes.forEach((carte) => {
      this.observateurCartes.unobserve(carte);
      this.observateurCartes.observe(carte);
    });
  }

  /* Les flèches du carrousel, sur ordinateur seulement (masquées au toucher par le CSS). */
  initFleches() {
    const bloc = this.querySelector(".pvg__arrows");
    if (!bloc) return;
    const prec = bloc.querySelector('[data-dir="-1"]');
    const suiv = bloc.querySelector('[data-dir="1"]');
    const pas = () => {
      const carte = this.cartes[0];
      return carte ? carte.getBoundingClientRect().width + 10 : 200;
    };
    prec.addEventListener("click", () => this.track.scrollBy({ left: -pas(), behavior: "smooth" }));
    suiv.addEventListener("click", () => this.track.scrollBy({ left: pas(), behavior: "smooth" }));

    const maj = () => {
      const max = this.track.scrollWidth - this.track.clientWidth;
      bloc.hidden = max < 4;
      prec.disabled = this.track.scrollLeft < 4;
      suiv.disabled = this.track.scrollLeft > max - 4;
    };
    this.track.addEventListener("scroll", maj, { passive: true });
    window.addEventListener("resize", maj);
    maj();
  }

  initLecteur() {
    const dialog = this.dialog;
    const video = this.video;

    dialog.querySelector(".pvg-player__close").addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => this.apresFermeture());

    // Sur ordinateur, un clic dans le noir autour de la vidéo ferme le lecteur.
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog || e.target.classList.contains("pvg-player__slide")) dialog.close();
    });

    this.slides.forEach((slide) => {
      slide.querySelector(".pvg-player__frame").addEventListener("click", () => this.basculer());
    });

    if (this.btnPrec) this.btnPrec.addEventListener("click", () => this.aller(this.indice - 1));
    if (this.btnSuiv) this.btnSuiv.addEventListener("click", () => this.aller(this.indice + 1));

    this.btnSon.addEventListener("click", () => {
      this.muet = !this.muet;
      video.muted = this.muet;
      if (!this.muet && video.paused) video.play().catch(() => {});
      this.majSon();
    });

    // La vidéo qui occupe l'écran prend le lecteur.
    this.observateurSlides = new IntersectionObserver((entrees) => {
      entrees.forEach((entree) => {
        if (entree.isIntersecting && dialog.open) this.activer(this.slides.indexOf(entree.target));
      });
    }, { root: this.slider, threshold: 0.6 });
    this.slides.forEach((slide) => this.observateurSlides.observe(slide));

    video.addEventListener("play", () => this.etat());
    video.addEventListener("pause", () => this.etat());
    video.addEventListener("timeupdate", () => this.progres());
    video.addEventListener("ended", () => {
      if (this.indice < this.slides.length - 1) this.aller(this.indice + 1);
    });

    // Écouté sur la page entière : quand le bouton qui avait le focus se désactive (dernière
    // vidéo), le focus retombe hors du lecteur et les flèches ne l'atteindraient plus.
    document.addEventListener("keydown", (e) => {
      if (!dialog.open) return;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        this.aller(this.indice + 1);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        this.aller(this.indice - 1);
      } else if (e.key === " " || e.key === "k") {
        if (e.target.closest && e.target.closest("button")) return; // l'espace garde son rôle sur un bouton
        e.preventDefault();
        this.basculer();
      } else if (e.key === "m") {
        this.btnSon.click();
      }
    });
  }

  ouvrir(i, carte) {
    this.retour = carte;
    this.prechargerAffiches();
    this.pauserApercus();
    document.documentElement.classList.add("pvg-lock");
    this.dialog.showModal();
    this.slider.scrollTop = this.slides[i].offsetTop;
    this.indice = -1;
    // Lancée ici, dans le clic de la cliente : le navigateur laisse passer le son.
    this.activer(i);
    this.indice = i;
    this.montrerIndice();
  }

  activer(i) {
    if (i < 0 || i >= this.slides.length || i === this.indice) return;
    this.indice = i;
    const slide = this.slides[i];
    const video = this.video;

    video.pause();
    const cadre = slide.querySelector(".pvg-player__frame");
    const affiche = cadre.querySelector(".pvg-player__poster");
    cadre.insertBefore(video, affiche ? affiche.nextSibling : cadre.firstChild);
    video.poster = slide.dataset.poster || "";
    video.src = slide.dataset.mp4;
    video.muted = this.muet;

    const essai = video.play();
    if (essai) {
      essai.catch((erreur) => {
        // Refus du son par le navigateur : on repart muet, le bouton du son reste là.
        if (erreur && erreur.name === "NotAllowedError" && !video.muted) {
          this.muet = true;
          video.muted = true;
          this.majSon();
          video.play().catch(() => this.etat());
        } else {
          this.etat();
        }
      });
    }

    this.slides.forEach((s, j) => s.classList.toggle("is-active", j === i));
    this.compteur.textContent = i + 1;
    if (this.btnPrec) this.btnPrec.disabled = i === 0;
    if (this.btnSuiv) this.btnSuiv.disabled = i === this.slides.length - 1;
    // Un bouton qui se désactive perd le focus : on le rend au lecteur.
    const actif = document.activeElement;
    if (this.dialog.open && (!actif || actif.disabled || !this.dialog.contains(actif))) {
      this.dialog.focus({ preventScroll: true });
    }
    this.majSon();
    this.progres();
  }

  aller(i) {
    if (i < 0 || i >= this.slides.length) return;
    const lisse = !matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.slider.scrollTo({ top: this.slides[i].offsetTop, behavior: lisse ? "smooth" : "auto" });
  }

  basculer() {
    const video = this.video;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  etat() {
    const slide = this.slides[this.indice];
    if (slide) slide.classList.toggle("is-paused", this.video.paused);
  }

  progres() {
    const slide = this.slides[this.indice];
    if (!slide) return;
    const barre = slide.querySelector(".pvg-player__bar span");
    const duree = this.video.duration;
    const part = duree && isFinite(duree) ? this.video.currentTime / duree : 0;
    if (barre) barre.style.transform = `scaleX(${part})`;
  }

  majSon() {
    this.dialog.classList.toggle("is-muted", this.muet);
    this.btnSon.setAttribute("aria-pressed", this.muet ? "true" : "false");
  }

  montrerIndice() {
    const indice = this.dialog.querySelector(".pvg-player__hint");
    if (!indice || this.indiceVu) return;
    this.indiceVu = true;
    requestAnimationFrame(() => indice.classList.add("is-visible"));
    setTimeout(() => indice.classList.remove("is-visible"), 3200);
  }

  apresFermeture() {
    const video = this.video;
    video.pause();
    video.removeAttribute("src");
    video.load();
    this.indice = -1;
    this.slides.forEach((s) => s.classList.remove("is-active", "is-paused"));
    document.documentElement.classList.remove("pvg-lock");
    this.reprendreApercus();
    if (this.retour) this.retour.focus({ preventScroll: true });
  }
}

if (!customElements.get("pvg-gallery")) customElements.define("pvg-gallery", VideoGalleryGrand);
